import {defer, requestIdleCallbackPollyfill, raf, isThrottled} from '@servicenow/ui-internal';
import {getConfigItem, configConstants} from '@servicenow/ui-config';
import {types as schedulerTypes} from '@servicenow/ui-metrics';
import Queue from './queue';
import metrics from './metrics';

const {ENABLE_SCHEDULER_STATS, SCHEDULER_RAF_TIMEOUT} = configConstants;
const enableSchedulerStats = getConfigItem(ENABLE_SCHEDULER_STATS);
const COMPONENT_RENDER = 'COMPONENT_RENDER';
const STATE_SET = 'STATE_SET';
const ACTION_HANDLER = 'ACTION_HANDLER';
const LEGACY_LIFECYCLE_FN = 'LEGACY_LIFECYCLE_FN';
const IMMEDIATE = 'IMMEDIATE';
const DEFER_IDLE = 'DEFER_IDLE';
const REFLECT_PROPERTIES = 'REFLECT_PROPERTIES';
const DISPATCH = 'DISPATCH';
const CLEANUP = 'CLEANUP';
const PROPERTY_SET = 'PROPERTY_SET';
const DEFER_HELPER_EXECUTION = 'DEFER_HELPER_EXECUTION';
const WHEN_COMPONENT_IS_IDLE = 'WHEN_COMPONENT_IS_IDLE';
const WHEN_ALL_COMPONENTS_IDLE = 'WHEN_ALL_COMPONENTS_IDLE';
const constants = {
	COMPONENT_RENDER,
	STATE_SET,
	ACTION_HANDLER,
	LEGACY_LIFECYCLE_FN,
	IMMEDIATE,
	DEFER_IDLE,
	REFLECT_PROPERTIES,
	DISPATCH,
	CLEANUP,
	PROPERTY_SET,
	DEFER_HELPER_EXECUTION,
	WHEN_COMPONENT_IS_IDLE,
	WHEN_ALL_COMPONENTS_IDLE
};

const P0 = 'PRIORITY_0';
const P0_5 = 'PRIORITY_0_5';
const P1 = 'PRIORITY_1';
const P2 = 'PRIORITY_2';
const P3 = 'PRIORITY_3';
const p0_5Queue = new Queue(false, []);
const p1Queue = new Queue(true, [], whenQueueIsEmptyForElement);
const p2Queue = new Queue(false, [], whenQueueIsEmptyForElement);
const p3Queue = new Queue(true, [WHEN_COMPONENT_IS_IDLE, WHEN_ALL_COMPONENTS_IDLE]);
const queueEmptyForElCallbacks = [];
let stats = {};

const types = {
	[IMMEDIATE]: {priority: P0},
	[DEFER_HELPER_EXECUTION]: {priority: P0_5},
	[COMPONENT_RENDER]: {priority: P1},
	[STATE_SET]: {priority: P2},
	[PROPERTY_SET]: {priority: P2},
	[ACTION_HANDLER]: {priority: P2},
	[LEGACY_LIFECYCLE_FN]: {priority: P2},
	[DISPATCH]: {priority: P2},
	[DEFER_IDLE]: {priority: P3},
	[REFLECT_PROPERTIES]: {priority: P3},
	[WHEN_COMPONENT_IS_IDLE]: {priority: P3},
	[WHEN_ALL_COMPONENTS_IDLE]: {priority: P3}
};

const idleTimeout = process.env.UNSAFE_SEISMIC_TASK_TIMEOUT ? 100 : -1;

let highPriorityPending = false;
let mediumPriorityPending = false;
let lowPriorityPending = false;

globalThis.nowUiFramework = globalThis.nowUiFramework || {};
globalThis.nowUiFramework.__getSchedulerStats = () =>
	Object.entries(stats)
		.sort(([priority1], [priority2]) => priority1.localeCompare(priority2))
		.map(
			([
				priority,
				{
					maxQueueDepth,
					totalQueued,
					totalQueueTime,
					maxQueueTime,
					totalExecutions,
					totalExecutionTime,
					maxExecutionTime
				}
			]) => ({
				priority,
				inQueue: {
					[P0]: 0,
					[P0_5]: p0_5Queue.length,
					[P1]: p1Queue.length,
					[P2]: p2Queue.length,
					[P3]: p3Queue.length
				}[priority],
				maxQueueDepth,
				totalQueued,
				averageQueueTime: totalQueueTime / totalQueued,
				maxQueueTime,
				totalExecutions,
				averageExecutionTime: totalExecutionTime / totalExecutions,
				maxExecutionTime
			})
		);
globalThis.nowUiFramework.__resetSchedulerStats = () => {
	stats = {};
};

const getDefaultStats = () => ({
	maxQueueDepth: 0,
	totalQueued: 0,
	totalQueueTime: 0,
	maxQueueTime: 0,
	totalExecutions: 0,
	totalExecutionTime: 0,
	maxExecutionTime: 0
});

const trackTaskScheduled = (task, priority, queue) => {
	if (!enableSchedulerStats) return;
	stats[priority] = stats[priority] || getDefaultStats();
	task.scheduled = performance.now();
	stats[priority].totalQueued++;
	const queueSize = queue ? queue.length : 0;
	if (queueSize > stats[priority].maxQueueDepth) stats[priority].maxQueueDepth = queueSize;
};

const trackTaskStart = (task, priority) => {
	if (!enableSchedulerStats) return;
	stats[priority] = stats[priority] || getDefaultStats();
	task.started = performance.now();
	const queueTime = task.started - task.scheduled;
	stats[priority].totalQueueTime += queueTime;
	if (queueTime > stats[priority].maxQueueTime) stats[priority].maxQueueTime = queueTime;
};

const trackTaskCompleted = (task, priority) => {
	if (!enableSchedulerStats) return;
	stats[priority] = stats[priority] || getDefaultStats();
	const executionTime = performance.now() - task.started;
	stats[priority].totalExecutionTime += executionTime;
	stats[priority].totalExecutions++;
	if (executionTime > stats[priority].maxExecutionTime)
		stats[priority].maxExecutionTime = executionTime;
};

function schedule(task) {
	const {type, fn, meta = {}} = task;
	if (!fn) throw new Error('a fn is required for scheduling a task');
	if (!type) throw new Error('a type is required for scheduling a task');

	const {
		[type]: {priority = P2}
	} = types;

	switch (priority) {
		case P0:
			metrics(meta, schedulerTypes.SCHEDULE, type, 'P0', []);
			trackTaskScheduled(task, P0);
			trackTaskStart(task, priority);
			fn(meta);
			trackTaskCompleted(task, priority);
			break;
		case P0_5:
			metrics(meta, schedulerTypes.SCHEDULE, type, 'P0_5', p0_5Queue);
			p0_5Queue.push(task);
			trackTaskScheduled(task, priority, p0_5Queue);
			runDeferredTask();
			break;
		case P1:
			metrics(meta, schedulerTypes.SCHEDULE, type, 'P1', p1Queue);
			p1Queue.push(task);
			trackTaskScheduled(task, priority, p1Queue);
			runHighPriorityTasks();
			break;
		case P2:
			metrics(meta, schedulerTypes.SCHEDULE, type, 'P2', p2Queue);
			p2Queue.push(task);
			trackTaskScheduled(task, priority, p2Queue);
			runMediumPriorityTasks();
			break;
		case P3:
			metrics(meta, schedulerTypes.SCHEDULE, type, 'P3', p3Queue);
			p3Queue.push(task);
			trackTaskScheduled(task, priority, p3Queue);
			runLowPriorityTasks();
			break;
	}
}

let isRendering = false;
const requestAnimationFrameTimeout = getConfigItem(SCHEDULER_RAF_TIMEOUT);

function runDeferredTask() {
	defer(() => {
		const task = p0_5Queue.shift();
		if (task) {
			metrics(task.meta, schedulerTypes.EXECUTE, task.type, 'P0_5', p0_5Queue);
			trackTaskStart(task, P0_5);
			task.fn(task.meta);
			trackTaskCompleted(task, P0_5);
		}
	}, 0);
}

function runHighPriorityTasks() {
	if (highPriorityPending) return;

	highPriorityPending = true;
	isRendering = true;
	const requestAnimationFrameTimeoutId = defer(() => {
		flushIdleQueues();
		isRendering = false;
	}, requestAnimationFrameTimeout);
	raf(() => {
		clearTimeout(requestAnimationFrameTimeoutId);
		let task;
		const start = Date.now();
		while ((task = p1Queue.shift())) {
			metrics(task.meta, schedulerTypes.EXECUTE, task.type, 'P1', p1Queue);
			trackTaskStart(task, P1);
			task.fn(task.meta);
			trackTaskCompleted(task, P1);
			if (Date.now() - start >= 100) break;
		}

		isRendering = false;
		highPriorityPending = false;
		if (p1Queue.length) runHighPriorityTasks();

		if (p2Queue.length) runMediumPriorityTasks();

		if (p3Queue.length) runLowPriorityTasks();
	});
}

function runMediumPriorityTasks() {
	if (mediumPriorityPending) return;
	if (isRendering) return;

	mediumPriorityPending = true;
	requestIdleCallbackPollyfill(
		deadline => {
			let task;

			while (deadline.timeRemaining() > 1 && (task = p2Queue.shift())) {
				metrics(task.meta, schedulerTypes.EXECUTE, task.type, 'P2', p2Queue);
				trackTaskStart(task, P2);
				task.fn(task.meta);
				trackTaskCompleted(task, P2);
			}

			mediumPriorityPending = false;
			if (p2Queue.length) runMediumPriorityTasks();

			if (p3Queue.length) runLowPriorityTasks();
		},
		{timeout: idleTimeout}
	);
}

function runLowPriorityTasks() {
	if (mediumPriorityPending) return;

	if (lowPriorityPending) return;

	if (isRendering) return;

	lowPriorityPending = true;
	requestIdleCallbackPollyfill(
		deadline => {
			let task;
			while (deadline.timeRemaining() > 1 && (task = p3Queue.shift())) {
				metrics(task.meta, schedulerTypes.EXECUTE, task.type, 'P3', p3Queue);
				trackTaskStart(task, P3);
				task.fn(task.meta);
				trackTaskCompleted(task, P3);
			}

			lowPriorityPending = false;
			if (p3Queue.length) runLowPriorityTasks();
		},
		{timeout: idleTimeout}
	);
}

function flushIdleQueues() {
	let task;
	while ((task = p0_5Queue.shift())) task.fn(task.meta);
	while ((task = p1Queue.shift())) task.fn(task.meta);
	highPriorityPending = false;
	while ((task = p2Queue.shift())) task.fn(task.meta);
	while ((task = p3Queue.shift())) task.fn(task.meta);
}

function isQueueFlushEnabled() {
	return (
		process.env.UNSAFE_SEISMIC_EMIT_CUSTOM_EVENT || window.nowUiFramework.queueFlushEnabled === true
	);
}

let flushing = false;
// eslint-disable-next-line no-inner-declarations
function flushQueues() {
	flushing = true;
	let task;

	while ((task = p0_5Queue.shift())) task.fn(task.meta);
	while ((task = p1Queue.shift())) task.fn(task.meta);
	while ((task = p2Queue.shift())) task.fn(task.meta);
	while ((task = p3Queue.shift())) task.fn(task.meta);

	defer(() => {
		if (p0_5Queue.length || p1Queue.length || p2Queue.length || p3Queue.length)
			return flushQueues();

		flushing = false;
		const event = new CustomEvent('SEISMIC_QUEUE_FLUSHED', {composed: true, bubbles: true});
		return document.dispatchEvent(event);
	}, 0);
}

document.addEventListener('FLUSH_SEISMIC_QUEUE', () => {
	if (!flushing && isQueueFlushEnabled()) flushQueues();
});

document.addEventListener(
	'visibilitychange',
	() => {
		if (isThrottled()) flushIdleQueues();
	},
	false
);

function countHighPriorityTasks(element) {
	return p1Queue.sizeForElement(element);
}

function countAllHighPriorityTasks() {
	return p1Queue.length;
}

function countAllMediumPriorityTasks() {
	return p2Queue.length;
}

function countMediumPriorityTasks(element) {
	return p2Queue.sizeForElement(element);
}

function addQueueEmptyForElCb(fn) {
	queueEmptyForElCallbacks.push(fn);
}

function whenQueueIsEmptyForElement(el) {
	const len = queueEmptyForElCallbacks.length;
	for (let i = 0; i < len; i++) queueEmptyForElCallbacks[i](el);
}

export {
	schedule,
	constants as types,
	countHighPriorityTasks,
	countMediumPriorityTasks,
	addQueueEmptyForElCb,
	countAllHighPriorityTasks,
	countAllMediumPriorityTasks
};
